import React, {useState} from 'react';
import {IconButton} from '@material-ui/core';
import {Roles} from 'app/Constants';
import {Column} from 'devextreme-react/data-grid';
import IntegrityRoleGrid from '../../../components/UserRole';
import {i18n2} from '../../i18n/i18n';
import useLoadSectorsForLookup from '../../sectors/hooks/useLoadLookupSectors';
import {
  useAddProfile,
  useDeleteProfile,
  useLoadProfiles,
  useUpdateProfile,
  useLoadLookupProfiles,
} from '../hooks/index';
import {ProfileSchema} from '../model/Profile';
import IntegrityProfileGrid from './IntegrityProfileGrid';
import GridConfig from './form/GridConfig';
import EditIcon from '@material-ui/icons/Edit';
import {AddCircle} from '@material-ui/icons';
import AddCoursePopup from './form/AddCoursePopup';
import {useToggle, useUtilInformation} from 'components/hooks';
import {useIsTablet} from 'components/hooks/useIsTablet';
import {
  getActionColumnWidth,
  useActionButtonStyles,
} from 'components/grid-dx/styles/actionButtonStyles';

const ProfilesGrid = () => {
  const loadProfiles = useLoadProfiles();
  const loadLookupProfiles = useLoadLookupProfiles();
  const addProfile = useAddProfile();
  const updateProfile = useUpdateProfile();
  const deleteProfile = useDeleteProfile();
  const {token, cwid, role} = useUtilInformation();
  const loadSectors = useLoadSectorsForLookup({token, cwid, role});
  const isTablet = useIsTablet();

  const CustomAddPopup = (open: any, setOpen: any, refresh: any, row: any) => {
    return (
      open && (
        <GridConfig
          open={open}
          handleClose={() => setOpen(false)}
          refresh={refresh}
          row={row}
          remoteAdd={addProfile}
          remoteChange={updateProfile}
        />
      )
    );
  };

  const MAX_ACTION_BUTTONS = 2;

  const Buttons = ({data, refresh}: any) => {
    const styles = useActionButtonStyles();
    const {open, toggle} = useToggle();
    const [openEdit, setOpenEdit] = useState(false);
    if (data === undefined || (data !== undefined && data.deleted)) return null;

    if (role === Roles.AdminQA) {
      return (
        <div
          className={
            isTablet ? styles.tabletContainer : styles.compactContainer
          }>
          <IconButton
            size="small"
            color={'primary'}
            title={i18n2._('Generar requerimiento')}
            onClick={toggle}
            className={isTablet ? styles.tabletButton : styles.compactButton}>
            <AddCircle
              className={isTablet ? styles.tabletIcon : styles.compactButton}
            />
          </IconButton>
          {open && (
            <AddCoursePopup
              open={open}
              setOpen={toggle}
              row={data}
              refresh={refresh}
            />
          )}

          <IconButton
            size="small"
            title="editar"
            onClick={() => setOpenEdit(true)}
            className={isTablet ? styles.tabletButton : styles.compactButton}>
            <EditIcon
              className={isTablet ? styles.tabletIcon : styles.compactButton}
            />
          </IconButton>
          {openEdit && (
            <GridConfig
              open={openEdit}
              handleClose={() => setOpenEdit(false)}
              refresh={refresh}
              row={data}
              remoteAdd={addProfile}
              remoteChange={updateProfile}
            />
          )}
        </div>
      );
    }

    if (role === Roles.AdminITPS) {
      return <EditButton row={data} refresh={refresh} />;
    }

    return null;
  };

  const EditButton = ({row, refresh}: any) => {
    const [open, setOpen] = useState(false);
    const styles = useActionButtonStyles();

    return (
      <div
        className={isTablet ? styles.tabletContainer : styles.compactContainer}>
        <IconButton
          size="small"
          title="editar"
          onClick={() => setOpen(true)}
          className={isTablet ? styles.tabletButton : styles.compactButton}>
          <EditIcon
            className={isTablet ? styles.tabletIcon : styles.compactButton}
          />
        </IconButton>
        {open && (
          <GridConfig
            open={open}
            handleClose={() => setOpen(false)}
            refresh={refresh}
            row={row}
            remoteAdd={addProfile}
            remoteChange={updateProfile}
          />
        )}
      </div>
    );
  };

  return (
    <IntegrityRoleGrid
      allowEdit={[Roles.AdminITPS, Roles.AdminQA]}
      schema={ProfileSchema()}
      customEditPopup={true}
      componentAddPopup={CustomAddPopup}
      dataSource={loadProfiles}
      promiseAdd={addProfile}
      promiseEdit={updateProfile}
      promiseDelete={deleteProfile}
      exportName="Profile-export"
      remoteLookup={[
        {
          column: 'iD_Sector',
          keyValue: 'iD_Sector',
          displayValue: 'name',
          dataSource: loadSectors,
        },
        {
          column: 'profileToCopy',
          keyValue: 'iD_Profile',
          displayValue: 'name',
          dataSource: loadLookupProfiles,
        },
      ]}
      id={['iD_Profile']}
      columnOptions={columnOptions}
      integrityGrid={IntegrityProfileGrid}
      integrityId={['ID_Profile']}
      integrityName={'Profile'}>
      <Column
        caption=""
        cellComponent={(e) => (
          <Buttons data={e.data.data} refresh={e.data.component.refresh} />
        )}
        fixed
        width={getActionColumnWidth(MAX_ACTION_BUTTONS, isTablet)}
        alignment="right"
      />
      <Column
        caption={i18n2._('#Profile')}
        hidingPriority={0}
        dataField="iD_Profile"
        width={100}
      />
      <Column
        caption={i18n2._('Nombre')}
        dataField="name"
        defaultSortOrder={'asc'}
      />
      <Column
        caption={i18n2._('#Sector')}
        hidingPriority={1}
        dataField="iD_Sector"
        width={100}
      />
      <Column
        caption={i18n2._('Nombre del sector')}
        dataField="sector_Name"
        width={200}
      />
      <Column
        caption={i18n2._('Meta4 Rol Id')}
        dataField="meta4RolId"
        allowEditing={false}
      />
      <Column
        caption={i18n2._('Meta4 Lugar Trabajo Id')}
        dataField="meta4LugarTrabajoId"
        allowEditing={false}
      />
      <Column caption={i18n2._('Revision')} dataField="revision" width={100} />
      <Column caption={i18n2._('Motivo')} dataField="reason" width={100} />
      <Column
        caption={i18n2._('Fecha de actualizacion')}
        dataField="updateDate"
        dataType="date"
        format="dd/MM/yyyy"
        calculateCellValue={(row: any) => new Date(row.updateDate)}
        width={100}
      />
      <Column
        caption={i18n2._('Actualizado por')}
        dataField="updatedBy"
        width={100}
      />
      <Column caption={i18n2._('Eliminado')} dataField="deleted" width={100} />
    </IntegrityRoleGrid>
  );
};

const columnOptions = [
  {
    column: 'iD_Profile',
    allowEdit: false,
    hiddenAdd: true,
    sizeMobile: 12,
    sizeDesktop: 12,
  },
  {
    column: 'iD_Sector',
    myLabel: 'Sector',
    sizeMobile: 12,
    sizeDesktop: 12,
  },
  {
    column: 'sector_Name',
    hiddenAdd: true,
    hiddenEdit: true,
  },
  {
    column: 'name',
    sizeMobile: 12,
    sizeDesktop: 12,
  },
  {
    column: 'meta4RolId',
    hiddenAdd: true,
    hiddenEdit: true,
    allowEdit: false,
    sizeMobile: 12,
    sizeDesktop: 12,
  },
  {
    column: 'meta4LugarTrabajoId',
    hiddenAdd: true,
    hiddenEdit: true,
    allowEdit: false,
    sizeMobile: 12,
    sizeDesktop: 12,
  },
  {
    column: 'profileToCopy',
    sizeMobile: 12,
    sizeDesktop: 12,
    selectType: 'limitedByColumn',
    allowDisabled: 'copyProfile',
    keyForFilter: 'iD_Sector',
    hiddenEdit: true,
  },
  {
    column: 'copyProfile',
    hiddenEdit: true,
  },
];

export default ProfilesGrid;
